import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicAccordion,
  CardComponent,
  Chip,
  GeneralCardComponent,
  InfoShield,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import {
  TextWithIcon,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import Divider from '../../atoms/Divider';

export interface ClauseInfoProps {
  type: 'Met' | 'Not Met' | 'InSufficient';
  title: string;
  tooltip?: string;
  summaryTitle?: string;
  summaryDescription?: string;
  analysisTitle?: string;
  analysisDescription?: string;
  extractsTitle?: string;
  extracts?: string[];
  extractsBody?: React.ReactNode;
  reasonsTitle?: string;
  reasons?: string[];
  reasonsBody?: React.ReactNode;
  clausesTitle?: string;
  clauses?: string[];
  content?: React.ReactNode;
  expandDefault?: boolean;
  isHeaderCard?: boolean;
  hideChip?: boolean;
  chipLabel?: string;
}

const ClauseInfo: React.FC<ClauseInfoProps> = ({
  title,
  tooltip,
  type,
  summaryTitle,
  summaryDescription,
  analysisTitle,
  analysisDescription,
  extractsTitle,
  extracts,
  reasonsTitle,
  reasons,
  clausesTitle,
  clauses,
  content,
  expandDefault,
  isHeaderCard,
  hideChip,
  reasonsBody,
  extractsBody,
  chipLabel,
}) => {
  const useStyles = makeStyles()({
    content: {
      borderTop: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      width: '100%',
      paddingTop: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    chipGrid: {
      position: 'absolute',
      right: '24px',
    },
    unorderList: {
      margin: 0,
      paddingLeft: '19px',
    },
  });

  const { classes } = useStyles();

  const getChipComponent = () => {
    switch (type) {
      case 'Not Met':
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={patientAccessTheme.palette.AIQChips?.textColor1}
              >
                {chipLabel ?? 'Not Met'}
              </Typography>
            }
            backgroundColor={patientAccessTheme.palette.AIQChips?.background1}
            border="0.5px solid rgba(226, 84, 84, 0.5)"
            borderRadius="4px"
            height="25px"
          />
        );
      case 'Met':
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={patientAccessTheme.palette.AIQChips?.textColor2}
              >
                {chipLabel ?? 'Met'}
              </Typography>
            }
            backgroundColor={patientAccessTheme.palette.AIQChips?.background2}
            border="0.5px solid rgba(0, 130, 114, 0.5)"
            borderRadius="4px"
            height="25px"
          />
        );
      case 'InSufficient':
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={'#cb8d23'}
              >
                {chipLabel ?? 'Insufficient Data'}
              </Typography>
            }
            backgroundColor={'rgba(203, 141, 35, 0.1)'}
            border="0.5px solid #e1a948"
            borderRadius="4px"
            height="25px"
            width={'115px'}
          />
        );
      default:
        return (
          <Chip
            label={
              <Typography
                fontSize="12px"
                lineHeight="15px"
                fontType="medium"
                textColor={patientAccessTheme.palette.grey[900]}
              >
                {chipLabel ?? 'NA'}
              </Typography>
            }
            backgroundColor={patientAccessTheme.palette.grey[300]}
            border={'0.3px solid' + patientAccessTheme.palette.grey[900]}
            borderRadius="4px"
            padding="5px 8px"
            height="25px"
            width={'150px'}
          />
        );
    }
  };

  const getTitleTypography = (label: string) => {
    return (
      <Typography
        fontSize="12px"
        lineHeight="140%"
        fontType="regular"
        textColor={patientAccessTheme.palette.text.lowemphasis}
        margin="0px 0px 8px 0px"
      >
        {label}
      </Typography>
    );
  };

  const getContentTypography = (label: string) => {
    return (
      <Typography
        fontSize="14px"
        lineHeight="24px"
        fontType="regular"
        textColor={patientAccessTheme.palette.text.highemphasis}
      >
        {label}
      </Typography>
    );
  };

  return (
    <BasicAccordion
      title={title}
      titleStyles={{ paddingRight: '180px' }}
      tooltip={tooltip}
      rightItem={
        hideChip ? (
          <></>
        ) : (
          <Grid className={classes.chipGrid}>{getChipComponent()}</Grid>
        )
      }
      accordionDetailsStyles={{ padding: '0px 24px 24px 24px' }}
      expandDefault={expandDefault}
      content={
        <Grid className={classes.content}>
          {content ? (
            content
          ) : (
            <>
              <Grid>
                {getTitleTypography(summaryTitle!)}
                {getContentTypography(summaryDescription!)}
              </Grid>
              {analysisTitle && (
                <Grid>
                  {analysisTitle && getTitleTypography(analysisTitle!)}
                  {analysisDescription &&
                    getContentTypography(analysisDescription!)}
                </Grid>
              )}
              {extractsTitle && (
                <CardComponent
                  title={getTitleTypography(extractsTitle!)}
                  mainGridStyles={{
                    background: patientAccessTheme.palette.grey[100],
                  }}
                  padding="20px"
                  body={
                    <Grid>
                      {extracts?.length !== 0
                        ? extracts?.map((label: string, index: number) => (
                            <>
                              {getContentTypography(label)}
                              {index !== extracts?.length - 1 && (
                                <Divider
                                  marginTop={'8px'}
                                  marginBottom={'8px'}
                                />
                              )}
                            </>
                          ))
                        : extractsBody}
                    </Grid>
                  }
                />
              )}
              {isHeaderCard ? (
                <GeneralCardComponent
                  title={
                    <TextWithIcon
                      startIcon={
                        <InfoShield
                          stroke={
                            type === 'InSufficient' ? '#cb8d23' : '#D6545E'
                          }
                          strokeWidth={0}
                        />
                      }
                      startIconRightMargin="4px"
                      label={
                        <Typography
                          fontSize="14px"
                          lineHeight="24px"
                          fontType="medium"
                          textColor={
                            type === 'InSufficient' ? '#cb8d23' : '#D6545E'
                          }
                        >
                          {reasonsTitle}
                        </Typography>
                      }
                    />
                  }
                  bodyStyles={{ padding: '16px' }}
                  headerStyles={{
                    background:
                      type === 'InSufficient'
                        ? 'rgba(203, 141, 35, 0.1) !important'
                        : 'rgba(214, 84, 94, 0.1) !important',
                    padding: '12px 16px',
                    height: '48px',
                  }}
                  body={
                    <ul className={classes.unorderList}>
                      {reasons?.map((label: string) => (
                        <li>{getContentTypography(label)}</li>
                      ))}
                    </ul>
                  }
                />
              ) : (
                reasonsTitle && (
                  <CardComponent
                    title={getTitleTypography(reasonsTitle!)}
                    padding="20px"
                    body={
                      <Grid>
                        {reasons?.length !== 0
                          ? reasons?.map((label: string, index: number) => (
                              <>
                                {getContentTypography(label)}
                                {index !== reasons?.length - 1 && (
                                  <Divider
                                    marginTop={'8px'}
                                    marginBottom={'8px'}
                                  />
                                )}
                              </>
                            ))
                          : reasonsBody}
                      </Grid>
                    }
                  />
                )
              )}
              {clausesTitle && (
                <CardComponent
                  title={getTitleTypography(clausesTitle!)}
                  padding="20px"
                  body={
                    <Grid>
                      {clauses?.map((label: string, index: number) => (
                        <>
                          {getContentTypography(label)}
                          {index !== clauses?.length - 1 && (
                            <Divider marginTop={'8px'} marginBottom={'8px'} />
                          )}
                        </>
                      ))}
                    </Grid>
                  }
                />
              )}
            </>
          )}
        </Grid>
      }
    />
  );
};

export default ClauseInfo;
